import React, {useId, useMemo, useState} from "react";
import {Card} from "react-bootstrap";
import {colorForGroup, formatNumber, formatPercent} from "./format.js";
import {formatPallets} from "./inventoryStrategyShared.jsx";
import {layoutRackRows, palletsNeed, warehouseCapacity} from "./LagerplaetzeLayout.js";

const FREE_FILL = "#f1f3f5";
const FREE_STROKE = "#adb5bd";
const AISLE_STROKE = "#ced4da";
const DOCK_FILL = "#fff3cd";
const DOCK_STROKE = "#e0b400";
const ARROW_COLOR = "#adb5bd";

// Mindestgröße in Metern, ab der ein Regalabschnitt eine Beschriftung trägt -
// kleinere Abschnitte bleiben unbeschriftet und zeigen ihre Angaben nur beim
// Überfahren (siehe Auftrag Punkt 4).
const LABEL_MIN_WIDTH_M = 3.2;
const LABEL_MIN_HEIGHT_M = 0.9;

const areaFormatter = new Intl.NumberFormat("de-DE", {minimumFractionDigits: 1, maximumFractionDigits: 1});
function formatArea(value) {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return "–";
    }
    return `${areaFormatter.format(value)} m²`;
}

function truncateLabel(text, widthMeters) {
    if (!text) {
        return "";
    }
    const maxChars = Math.max(3, Math.round(widthMeters * 2.4));
    return text.length > maxChars ? `${text.slice(0, maxChars - 1)}…` : text;
}

/**
 * Eine Halle als massstabsgetreues SVG mit einem echten Regallayout:
 * Regalzeilen längs der Halle (siehe layoutRackRows() in
 * LagerplaetzeLayout.js), dazwischen die Arbeitsgänge für den Stapler, an
 * beiden Enden die Quergänge - der erste zugleich als Zone für
 * Wareneingang/-ausgang gekennzeichnet. Die zugeteilten Sortimente füllen
 * die Regalreihen abschnittsweise, statt frei über die Fläche verteilt zu
 * sein (Auftrag Punkt 1 und 4). `pxPerMeter` ist über alle Hallen identisch,
 * damit die Flächen zueinander massstabsgetreu bleiben.
 *
 * Unterhalb `lg` (992 px) wird die Grafik selbst unbrauchbar - eine 120 m
 * lange Halle auf rund 330 px gepresst lässt einzelne Regalabschnitte weder
 * unterscheiden noch treffsicher antippen. Sie bleibt deshalb auf schmalen
 * Fenstern standardmäßig eingeklappt (über einen Schalter weiterhin
 * erreichbar) und wird durch eine Liste der Sortimente dieser Halle ersetzt -
 * dieselben Angaben wie im Grafik-Tooltip, ohne Maus oder treffsicheren
 * Fingertipp zu brauchen. Ab `lg` bleibt die Grafik unverändert immer
 * sichtbar.
 */
export default function LagerplaetzeFloorPlan({warehouse, assignedRows, assignedPallets, colorMap, pxPerMeter, onHoverBlock, onLeaveBlock}) {
    const {length, width} = warehouse;
    const patternId = useId();
    const arrowId = useId();
    const [showGraphic, setShowGraphic] = useState(false);

    const {layout, blocks, aisles} = useMemo(() => layoutRackRows(assignedRows, warehouse), [assignedRows, warehouse]);
    const {modules, slotsPerRow, slotsPerLevel, levels, totalArea, rackArea, trafficArea, trafficPercent} = layout;
    const totalCapacity = warehouseCapacity(warehouse);
    const datei = warehouse.capacityFile || null;

    const free = Math.max(0, totalCapacity - assignedPallets);
    const utilization = totalCapacity > 0 ? (assignedPallets / totalCapacity) * 100 : 0;
    const nearlyFull = utilization >= 95;

    const displayWidth = Math.max(230, Math.round(length * pxPerMeter));
    const displayHeight = Math.max(90, Math.round(displayWidth * (width / Math.max(length, 0.01))));

    // Für die mobile Sortimentsliste dieselben Zeilen wie in der Grafik,
    // absteigend nach empfohlenem Palettenbedarf sortiert - deckungsgleich
    // mit der optischen Gewichtung der Regalabschnitte.
    const sortedAssignedRows = useMemo(
        () => [...(assignedRows || [])].sort((a, b) => palletsNeed(b) - palletsNeed(a)),
        [assignedRows]
    );

    return (
        <Card className="shadow-sm" style={{width: displayWidth, maxWidth: "100%"}}>
            <Card.Body className="d-flex flex-column gap-2">
                <div>
                    <div className="fw-semibold d-flex align-items-center gap-2 flex-wrap">
                        {warehouse.name}
                        <span className="badge bg-body-secondary text-body fw-normal">
                            {formatNumber(length)} × {formatNumber(width)} m
                        </span>
                    </div>
                    {warehouse.note && <div className="small text-muted">{warehouse.note}</div>}
                </div>

                <div className="d-flex flex-column gap-1 small">
                    <div className="d-flex justify-content-between gap-2">
                        <span className="text-muted text-nowrap">{datei ? "Kapazität (gezählt)" : "Kapazität (errechnet)"}</span>
                        <span className="fw-semibold text-nowrap">{formatNumber(totalCapacity)} Pl.</span>
                    </div>
                    <div className="d-flex justify-content-between gap-2">
                        <span className="text-muted text-nowrap">Belegt (Empf.)</span>
                        <span className="fw-semibold text-nowrap">{formatPallets(assignedPallets)}</span>
                    </div>
                    <div className="d-flex justify-content-between gap-2">
                        <span className="text-muted text-nowrap">Frei</span>
                        <span className="fw-semibold text-nowrap">{formatPallets(free)}</span>
                    </div>
                    <div className="d-flex justify-content-between gap-2">
                        <span className="text-muted text-nowrap">Auslastung</span>
                        <span className={`fw-semibold text-nowrap ${nearlyFull ? "text-danger" : ""}`}>
                            {formatPercent(utilization, 0)}
                            {nearlyFull && <span className="ms-1">(fast voll)</span>}
                        </span>
                    </div>
                </div>

                <div className="d-lg-none d-flex align-items-center justify-content-between gap-2">
                    <span className="small text-muted">
                        Grafik auf schmalen Fenstern eingeklappt - Sortimente stehen als Liste unten.
                    </span>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary text-nowrap"
                        aria-expanded={showGraphic}
                        onClick={() => setShowGraphic((value) => !value)}
                    >
                        {showGraphic ? "Grafik ausblenden" : "Grafik anzeigen"}
                    </button>
                </div>

                <svg
                    viewBox={`0 0 ${length} ${width}`}
                    width={displayWidth}
                    height={displayHeight}
                    className={showGraphic ? "d-block d-lg-block" : "d-none d-lg-block"}
                    style={{maxWidth: "100%", height: "auto", border: "1px solid #dee2e6", borderRadius: 4, background: "#fff"}}
                    role="img"
                    aria-label={`Regallayout ${warehouse.name}: ${formatPercent(utilization, 0)} ausgelastet, ${formatPercent(trafficPercent, 0)} Verkehrsfläche`}
                >
                    <defs>
                        <pattern id={patternId} width="1.4" height="1.4" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                            <line x1="0" y1="0" x2="0" y2="1.4" stroke={AISLE_STROKE} strokeWidth="0.05" />
                        </pattern>
                        <marker id={arrowId} markerWidth="4" markerHeight="4" refX="2" refY="2" orient="auto">
                            <path d="M0,0 L4,2 L0,4 Z" fill={ARROW_COLOR} />
                        </marker>
                    </defs>

                    {/* Grundfläche der Halle - alles, was kein Regal ist, ist Verkehrsfläche */}
                    <rect x={0} y={0} width={length} height={width} fill={`url(#${patternId})`} />

                    {/* Quergänge an den Hallenenden, der erste als Wareneingang/-ausgang markiert */}
                    {aisles
                        .filter((aisle) => aisle.role !== "arbeitsgang")
                        .map((aisle, index) => {
                            const isDock = aisle.role === "wareneingang";
                            return (
                                <g key={`end-${index}`}>
                                    <rect
                                        x={aisle.x}
                                        y={aisle.y}
                                        width={aisle.w}
                                        height={aisle.h}
                                        fill={isDock ? DOCK_FILL : "#fff"}
                                        fillOpacity={isDock ? 0.9 : 0}
                                        stroke={isDock ? DOCK_STROKE : "none"}
                                        strokeWidth={isDock ? 0.12 : 0}
                                    />
                                    {aisle.w > 2 && aisle.h > 6 && (
                                        <line
                                            x1={aisle.x + aisle.w / 2}
                                            y1={aisle.y + aisle.h * 0.12}
                                            x2={aisle.x + aisle.w / 2}
                                            y2={aisle.y + aisle.h * 0.88}
                                            stroke={ARROW_COLOR}
                                            strokeWidth="0.12"
                                            strokeDasharray="0.8 0.6"
                                            markerEnd={`url(#${arrowId})`}
                                        />
                                    )}
                                    {isDock && aisle.h > 8 && (
                                        <text
                                            x={aisle.x + aisle.w / 2}
                                            y={aisle.y + aisle.h / 2}
                                            textAnchor="middle"
                                            dominantBaseline="middle"
                                            transform={`rotate(-90 ${aisle.x + aisle.w / 2} ${aisle.y + aisle.h / 2})`}
                                            fontSize={Math.min(1.4, aisle.w * 0.9)}
                                            fill="#5c4a00"
                                            style={{fontWeight: 600}}
                                        >
                                            Wareneingang / Warenausgang
                                        </text>
                                    )}
                                </g>
                            );
                        })}

                    {/* Arbeitsgänge zwischen den Regalmodulen */}
                    {aisles
                        .filter((aisle) => aisle.role === "arbeitsgang")
                        .map((aisle, index) =>
                            aisle.w > 6 ? (
                                <line
                                    key={`aisle-${index}`}
                                    x1={aisle.x + aisle.w * 0.06}
                                    y1={aisle.y + aisle.h / 2}
                                    x2={aisle.x + aisle.w * 0.94}
                                    y2={aisle.y + aisle.h / 2}
                                    stroke={ARROW_COLOR}
                                    strokeWidth="0.1"
                                    strokeDasharray="1 0.8"
                                    markerEnd={`url(#${arrowId})`}
                                />
                            ) : null
                        )}

                    {/* Regalreihen: belegte Abschnitte je Sortiment, dazwischen freie Reihenmeter */}
                    {blocks.map((block) => {
                        const isFree = Boolean(block.isFree);
                        const fill = isFree ? FREE_FILL : colorForGroup(colorMap, block.article_group);
                        const canLabel = !isFree && block.w >= LABEL_MIN_WIDTH_M && block.h >= LABEL_MIN_HEIGHT_M;
                        const fontSize = Math.max(0.28, Math.min(0.55, block.h * 0.55));

                        const handleHover = (clientX, clientY) => {
                            if (isFree) {
                                return;
                            }
                            onHoverBlock(block, {clientX, clientY}, warehouse, totalCapacity);
                        };

                        return (
                            <g
                                key={block.id}
                                tabIndex={isFree ? undefined : 0}
                                role={isFree ? undefined : "button"}
                                aria-label={
                                    isFree
                                        ? undefined
                                        : `${block.parent_name}, Übernummer ${block.parent_article_number}, Regalreihe ${block.rowNumber}, ${formatPallets(palletsNeed(block))} empfohlen`
                                }
                                onMouseEnter={(event) => handleHover(event.clientX, event.clientY)}
                                onMouseMove={(event) => handleHover(event.clientX, event.clientY)}
                                onMouseLeave={() => !isFree && onLeaveBlock()}
                                onClick={(event) => handleHover(event.clientX, event.clientY)}
                                onFocus={(event) => {
                                    const rect = event.currentTarget.getBoundingClientRect();
                                    handleHover(rect.left + rect.width / 2, rect.top + rect.height / 2);
                                }}
                                onBlur={() => !isFree && onLeaveBlock()}
                                style={{cursor: isFree ? "default" : "pointer", outline: "none"}}
                            >
                                <rect
                                    x={block.x}
                                    y={block.y}
                                    width={block.w}
                                    height={block.h}
                                    fill={fill}
                                    stroke={isFree ? FREE_STROKE : "#ffffff"}
                                    strokeWidth={isFree ? 0.08 : 0.06}
                                    strokeDasharray={isFree ? "0.4 0.3" : undefined}
                                />
                                {canLabel && (
                                    <text
                                        x={block.x + block.w / 2}
                                        y={block.y + block.h / 2}
                                        textAnchor="middle"
                                        dominantBaseline="middle"
                                        fontSize={fontSize}
                                        fill="#fff"
                                        style={{
                                            pointerEvents: "none",
                                            fontWeight: 600,
                                            paintOrder: "stroke",
                                            stroke: "rgba(0,0,0,.45)",
                                            strokeWidth: fontSize * 0.08,
                                        }}
                                    >
                                        {truncateLabel(block.parent_name, block.w)}
                                    </text>
                                )}
                            </g>
                        );
                    })}
                </svg>

                {/* Sortimentsliste statt Grafik auf schmalen Fenstern (< lg) - dieselben
                    Angaben wie im Grafik-Tooltip (Name, Warengruppe, Paletten, Anteil an
                    der Hallenkapazität), ohne Maus oder treffsicheren Fingertipp auf einen
                    wenige Pixel breiten Regalabschnitt zu brauchen. */}
                <div className="d-lg-none">
                    <div className="small fw-semibold text-muted mb-2">Sortimente in dieser Halle</div>
                    {sortedAssignedRows.length === 0 ? (
                        <div className="small text-muted">Dieser Halle sind keine Sortimente zugeteilt.</div>
                    ) : (
                        <ul className="list-unstyled d-flex flex-column gap-2 mb-0">
                            {sortedAssignedRows.map((row) => {
                                const share = totalCapacity > 0 ? (palletsNeed(row) / totalCapacity) * 100 : 0;
                                return (
                                    <li
                                        key={row.parent_article_number || row.article_group || row.parent_name}
                                        className="d-flex align-items-start gap-2 border rounded-2 p-2"
                                    >
                                        <span
                                            aria-hidden="true"
                                            style={{
                                                width: 10,
                                                height: 10,
                                                borderRadius: 3,
                                                marginTop: 4,
                                                flexShrink: 0,
                                                backgroundColor: colorForGroup(colorMap, row.article_group),
                                            }}
                                        />
                                        <div className="flex-grow-1">
                                            <div className="fw-semibold small">{row.parent_name || row.parent_article_number || "Ohne Bezeichnung"}</div>
                                            <div className="text-muted" style={{fontSize: "0.75rem"}}>
                                                {row.parent_article_number} · {row.article_group || "Ohne Warengruppe"}
                                            </div>
                                        </div>
                                        <div className="text-end small text-nowrap">
                                            <div className="fw-semibold">{formatPallets(palletsNeed(row))}</div>
                                            <div className="text-muted">{formatPercent(share, 0)}</div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>

                {datei ? (
                    <div className="small text-muted">
                        <div>
                            <strong>{formatNumber(datei.kapazitaet)} Palettenplätze</strong> laut Kapazitätsdatei Lahr
                            {datei.kapazitaet > 0
                                ? <> ({formatNumber(datei.regal)} im Regal{datei.boden > 0 ? <>, {formatNumber(datei.boden)} am Boden</> : null}, {formatNumber(datei.reihen)} {datei.reihen === 1 ? "Regalreihe" : "Regalreihen"})</>
                                : <> - keine Palettenregale</>}
                            {datei.stand && <>, Stand {datei.stand}</>}.
                        </div>
                        {datei.hoehenklassen.length > 0 && (
                            <div>Nach Ladehöhe: {datei.hoehenklassen.map(({label, plaetze}) => `${label} ${formatNumber(plaetze)}`).join(" · ")}</div>
                        )}
                        {datei.zonen.length > 0 && (
                            <div>Nach Bereich: {datei.zonen.map(({name, plaetze}) => `${name} ${formatNumber(plaetze)}`).join(" · ")}</div>
                        )}
                        <div>
                            Die Zeichnung ist schematisch: Regalreihen nach dem Rechenmodell, belegt mit den gezählten Plätzen.
                            Gesamtfläche {formatArea(totalArea)}.
                        </div>
                    </div>
                ) : (
                <div className="small text-muted">
                    <div>
                        {formatNumber(modules)} Regalmodule × 2 Reihen × {formatNumber(slotsPerRow)} Stellplätze/Reihe ={" "}
                        {formatNumber(slotsPerLevel)} Plätze je Ebene × {formatNumber(levels)} Ebenen ={" "}
                        <strong>{formatNumber(totalCapacity)} Plätze</strong>
                    </div>
                    <div>
                        Gesamtfläche {formatArea(totalArea)} · davon Regalfläche {formatArea(rackArea)} · davon
                        Verkehrsfläche {formatArea(trafficArea)} ({formatPercent(trafficPercent, 0)})
                    </div>
                </div>
                )}
            </Card.Body>
        </Card>
    );
}
